import { TenantConfig } from "../types/tenantConfig";

/**
 * Module profile used by the local dev override below. Both use cases get the
 * same steps — the point of the override is to see the flow at all, not to
 * simulate a tenant's per-use-case differences.
 */
const DEV_FLOW_MODULES = {
  welcomeInfo: true,
  houseRules: true,
  assetReference: true,
  startChecklist: false,
  endChecklist: false,
  checkOutPhoto: false,
} as const;

export const DefaultConfig: TenantConfig = {
  api: {
    baseUrl: process.env.REACT_APP_API_BASE_URL!,
    key: process.env.REACT_APP_API_KEY!,
  },
  seo: {
    appTitle: "software for smart sharing",
    appDescription:
      "kolula ermöglicht kontaktlose Übergabe von Waren, den einfachen Zugang zu Produkten und sichereres temporäres Verwahren von Gegenständen.",
    ogTitle: "Software für smartes Sharing",
    ogDescription:
      "kolula ermöglicht kontaktlose Übergabe von Waren, den einfachen Zugang zu Produkten und sichereres temporäres Verwahren von Gegenständen.",
    ogImage:
      "https://www.kolula.com/wp-content/uploads/2024/08/branchen-uebersicht-1240x636.jpg",
    ogUrl: "https://www.kolula.com/",
  },
  languages: ["en", "de"],
  fallbackLng: "en",
  currency: "EUR",
  currencySymbol: "€",
  paymentProvider: ["no_payment"],

  phoneInput: {
    defaultCountry: "de",
    preferredCountries: ["de", "at", "ch", "nl", "be"],
  },

  contact: {
    phone: "0211 542435 40",
    phoneFormatted: "0211 542435 40",
  },
  toolbarItems: ["locations", "orders", "info"],

  allowCoupons: true,
  allowNewsLetter: false,
  consentRequired: true,
  services: ["rental", "p2p", "apartment", "storage", "sup"],
  isEnableLocationVerification: true,
  gracePeriodMinutes: 0,
  authentication: "none",
  skipPayment: false,
  disableCoupon: false,
  // H-3a end-user accounts (OTP login) — dark-shipped, OFF by default. The
  // runtime tenant config (mapPublicConfig) spreads DefaultConfig, so no
  // tenant sees the account UI until the backend flag exists and is enabled.
  accountsEnabled: false,
  // Generic check-in/check-out flow. OFF by default: the runtime tenant config
  // (mapPublicConfig) spreads DefaultConfig, so a default that switched the
  // (mock-backed) generic flow on would do it for every venue/apartment
  // tenant. Local testing opts in via REACT_APP_GENERIC_FLOW=true (paired with
  // REACT_APP_MOCK_USAGE_LOG=true). Per-use-case enablement is owned by the
  // admin self-config editor + backend.
  checkInCheckOutFlow: {
    // Per use case since 2026-09-03 — there is no tenant-level `enabled` any
    // more. The dev override covers the two use cases the generic flow serves,
    // so REACT_APP_GENERIC_FLOW behaves exactly as it did locally; production
    // reads the stored map from the public tenant config and this default is
    // then fully replaced.
    useCases:
      process.env.REACT_APP_GENERIC_FLOW === "true"
        ? {
            venue: { enabled: true, modules: DEV_FLOW_MODULES },
            apartment: { enabled: true, modules: DEV_FLOW_MODULES },
          }
        : {},
    // Access management (Decision 2) — digital lock add-on. OFF by default with
    // the same default-off discipline as the flow `enabled` flag: the runtime
    // tenant config spreads DefaultConfig and the backend does not yet return
    // the block, so a hardcoded `true` would silently switch the hardware
    // modules on for every venue/apartment tenant. Local testing opts in via
    // REACT_APP_ACCESS_MGMT=true. Per-tenant enablement is owned by the admin
    // self-config editor + backend + the `accessManagement` entitlement.
    // PRESENT ONLY under the dev flag. Shipping `{enabled:false,
    // locationCheck:false}` in production made the client default
    // indistinguishable from a tenant who had configured "off": the runtime
    // config spreads DefaultConfig, so `accessManagement` was ALWAYS an object,
    // and `resolveLocationCheck` (modules/useOrder/locationCheck.ts) read that
    // as a configured answer and switched the QR location check off for every
    // runtime tenant — including ones that had never configured anything.
    // Absent now means unconfigured, which is what every reader already assumes
    // (`GenericUseOrderFlow` uses `accessManagement?.enabled === true`).
    ...(process.env.REACT_APP_ACCESS_MGMT === "true"
      ? { accessManagement: { enabled: true, locationCheck: false } }
      : {}),
  },
};

export default DefaultConfig;
